import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  Renderer2,
} from '@angular/core';
import { IBANCulture } from './iban-culture';
import { IBAN_BLOCKS } from './iban-blocks';
import { IBANDetail } from '../../models/iban-detail-model';

@Directive({
  selector: '[kfmIbanFormat]',
})
export class IbanFormatDirective implements AfterViewInit {
  private readonly _elementRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);

  private _maxLength = 34;
  private _placeholder = '';

  private _ibanDetail: IBANDetail = new IBANDetail(IBANCulture.DE, '');

  @Input() set ibanDetail(val: IBANDetail) {
    if (val) {
      this._ibanDetail = val;
      this._maxLength = val?.ibanLength ?? 34;
    }
    const inputElement = this._elementRef.nativeElement as HTMLInputElement;
    this.updatePlaceholder(inputElement);
    if (inputElement && this._ibanDetail?.iban) {
      inputElement.value = this._ibanDetail.iban;
    }
  }

  ngAfterViewInit() {
    // Change the placeholder of the input element within the mat-form-field
    const inputElement = this._elementRef.nativeElement as HTMLInputElement;
    this.updatePlaceholder(inputElement);
    inputElement.value = this._ibanDetail.iban;
  }

  @HostListener('input')
  onInput() {
    const inputElement = this._elementRef.nativeElement as HTMLInputElement;
    const cursorPosition = inputElement.selectionStart ?? 0;
    const unformattedCursorPosition = inputElement.value
      .slice(0, cursorPosition)
      .replace(/\s/g, '').length;

    let value = inputElement.value.replace(/\s/g, '').toUpperCase();

    // Remove non-alphanumeric characters
    value = value.replace(/[^A-Z0-9]/g, '');

    this.updateFormatFromValue(value, inputElement);

    // Trim to max length
    value = value.substring(0, this._maxLength);

    // Fill with underscores
    const fullValue = value.padEnd(this._maxLength, '_');

    // Group by 4 characters
    const grouped = fullValue.match(/.{1,4}/g)?.join(' ') ?? '';

    // Set the formatted value
    this._renderer.setProperty(inputElement, 'value', grouped);

    // After formatting, adjust the caret position
    const newCaretPosition = this.getCaretPosition(
      unformattedCursorPosition,
      grouped,
    );
    inputElement.setSelectionRange(newCaretPosition, newCaretPosition);

    return value;
  }

  private updateFormatFromValue(value: string, inputElement: HTMLInputElement) {
    const countryCode = value.slice(0, 2) as keyof typeof IBAN_BLOCKS;
    const blocks = IBAN_BLOCKS[countryCode] ?? this._ibanDetail.printFormat;

    this._maxLength = blocks.reduce((sum, block) => sum + block, 0);
    this.updatePlaceholderFromBlocks(blocks, inputElement);
  }

  // Helper method to calculate the caret position
  private getCaretPosition(
    unformattedCursorPosition: number,
    formattedValue: string,
  ): number {
    if (unformattedCursorPosition <= 0) {
      return 0;
    }

    let visibleCharacterCount = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      if (formattedValue[i] !== ' ') {
        visibleCharacterCount++;

        if (visibleCharacterCount === unformattedCursorPosition) {
          return i + 1;
        }
      }
    }

    return formattedValue.length;
  }

  private setPlaceholder() {
    this.updatePlaceholderFromBlocks(this._ibanDetail.printFormat);
  }

  private updatePlaceholderFromBlocks(
    blocks: Array<number>,
    inputElement?: HTMLInputElement,
  ) {
    this._placeholder = '';

    for (let i = 0; i < blocks.length; i++) {
      for (let j = 0; j < blocks[i]; j++) {
        this._placeholder += '_';
      }

      this._placeholder += ' ';
    }

    this._placeholder = this._placeholder.trim();

    if (inputElement) {
      inputElement.placeholder = this._placeholder;
    }
  }

  // Update the placeholder programmatically
  private updatePlaceholder(inputElement: HTMLInputElement): void {
    this.setPlaceholder();
    inputElement.placeholder = this._placeholder;
  }
}
